import { API_BASE_URL } from '@/lib/apiBase';

const ANALYTICS_PATH = '/analytics/events';
const VISITOR_ID_KEY = 'analytics_visitor_id';
const SESSION_ID_KEY = 'analytics_session_id';
const SESSION_ENTRY_TRACKED_KEY = 'analytics_entry_tracked';

export interface TrackOptions {
  /** Only send an event for the first page load of the session (the landing), not for in-app navigation. */
  entryOnly?: boolean;
  /** Only send an event when at least one utm_ parameter is present. */
  requireUtm?: boolean;
}

export interface AnalyticsEventPayload {
  tenant: string;
  eventType: 'page_view';
  path: string;
  slug?: string;
  title?: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  visitorId?: string;
  sessionId?: string;
  occurredAt: string;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

let lastTrackedPageKey: string | null = null;

function createId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getStorage(kind: 'localStorage' | 'sessionStorage'): StorageLike | null {
  if (typeof window === 'undefined') return null;

  try {
    return window[kind];
  } catch {
    return null;
  }
}

export function getOrCreateAnonymousId(
  storage: StorageLike | null,
  key: string,
): string | undefined {
  if (!storage) return createId();

  try {
    const existing = storage.getItem(key);
    if (existing) return existing;

    const created = createId();
    storage.setItem(key, created);
    return created;
  } catch {
    return createId();
  }
}

export function getVisitorId(): string | undefined {
  return getOrCreateAnonymousId(getStorage('localStorage'), VISITOR_ID_KEY);
}

export function getSessionId(): string | undefined {
  return getOrCreateAnonymousId(getStorage('sessionStorage'), SESSION_ID_KEY);
}

export function hasAnyUtmParam(
  payload: Pick<AnalyticsEventPayload, 'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmTerm' | 'utmContent'>,
): boolean {
  return [payload.utmSource, payload.utmMedium, payload.utmCampaign, payload.utmTerm, payload.utmContent]
    .some(v => v !== undefined);
}

export function readUtmParams(search: string): Pick<
  AnalyticsEventPayload,
  'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmTerm' | 'utmContent'
> {
  const params = new URLSearchParams(search);

  return {
    utmSource: params.get('utm_source') ?? undefined,
    utmMedium: params.get('utm_medium') ?? undefined,
    utmCampaign: params.get('utm_campaign') ?? undefined,
    utmTerm: params.get('utm_term') ?? undefined,
    utmContent: params.get('utm_content') ?? undefined,
  };
}

export function buildPageViewPayload(input: {
  path: string;
  slug?: string;
  title?: string;
  referrer?: string;
  search?: string;
  occurredAt?: string;
}): AnalyticsEventPayload {
  return {
    tenant: import.meta.env.VITE_TENANT,
    eventType: 'page_view',
    path: input.path,
    slug: input.slug,
    title: input.title,
    referrer: input.referrer,
    visitorId: getVisitorId(),
    sessionId: getSessionId(),
    occurredAt: input.occurredAt ?? new Date().toISOString(),
    ...readUtmParams(input.search ?? ''),
  };
}

export function shouldTrackPageView(
  payload: Pick<AnalyticsEventPayload, 'path' | 'slug' | 'title' | 'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmTerm' | 'utmContent'>,
  options: TrackOptions = {},
): boolean {
  if (options.entryOnly) {
    // Only track the landing: first page load of the session
    const session = getStorage('sessionStorage');
    if (session !== null) {
      try {
        if (session.getItem(SESSION_ENTRY_TRACKED_KEY) !== null) return false;
        session.setItem(SESSION_ENTRY_TRACKED_KEY, '1');
      } catch {
        // ignore storage errors
      }
    }
  } else {
    // Without entryOnly, deduplicate by page identity to prevent double-fires
    const key = JSON.stringify({ path: payload.path, slug: payload.slug, title: payload.title });
    if (key === lastTrackedPageKey) return false;
    lastTrackedPageKey = key;
  }

  if (options.requireUtm && !hasAnyUtmParam(payload)) return false;

  return true;
}

export async function sendAnalyticsEvent(payload: AnalyticsEventPayload): Promise<void> {
  const url = `${API_BASE_URL}${ANALYTICS_PATH}`;
  const body = JSON.stringify(payload);

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }

    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('Analytics event delivery failed.', error);
    }
  }
}

export async function trackPageView(
  input: { path: string; slug?: string; title?: string; referrer?: string; search?: string },
  options: TrackOptions = {},
): Promise<void> {
  const payload = buildPageViewPayload(input);
  if (!shouldTrackPageView(payload, options)) return;
  await sendAnalyticsEvent(payload);
}

export function resetAnalyticsStateForTests(): void {
  lastTrackedPageKey = null;
}
